import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { apiFetch, API_URL } from '../../services/apiClient';
import {
  FaTrophy, FaFire, FaRocket, FaShieldAlt, FaStar, FaMedal, FaCoins,
  FaCheck, FaTimes, FaLock, FaInfoCircle, FaIdBadge, FaBolt, FaUserCircle, FaTasks,
} from 'react-icons/fa';
import { TbCrown } from 'react-icons/tb';
import { MdEmojiEvents } from 'react-icons/md';
import './GameRewards.css';
import {
  levelInfo, levelColor, initials, avatarColor,
  EMBLEMS, FRAMES, emblemLabel, frameLabel,
} from '../../lib/achievements';
import { Avatar, TrophyShowcase } from '../Achievements/Achievements';

// XP por evento (espejo de lib/gamification.js) — guía "Cómo ganar XP".
const XP_EARN = [
  { action: 'Trade completado',          xp: 150, icon: FaCheck },
  { action: 'Racha de 7 días',           xp: 200, icon: FaFire },
  { action: 'Racha de 30 días',          xp: 500, icon: FaFire },
  { action: 'Verificar identidad (KYC)', xp: 300, icon: FaIdBadge },
  { action: 'Verificar email',           xp: 100, icon: FaCheck },
  { action: 'Reseña 5★ recibida',        xp: 50,  icon: FaStar },
  { action: 'Publicar una oferta',       xp: 10,  icon: FaRocket },
];

function AvatarTab({ u }) {
  const { updateUser } = useAuth();
  const [emblem, setEmblem] = useState(u.avatarEmblem || 'photo');
  const [frame, setFrame]   = useState(u.avatarFrame || 'basic');
  const [saved, setSaved]   = useState(false);
  const dirty = emblem !== (u.avatarEmblem || 'photo') || frame !== (u.avatarFrame || 'basic');
  // Equipar: feedback optimista + persistencia real en backend (antes solo localStorage).
  const equip = async () => {
    updateUser({ avatarEmblem: emblem, avatarFrame: frame });
    setSaved(true); setTimeout(() => setSaved(false), 2200);
    try {
      const res = await apiFetch('/api/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatarEmblem: emblem, avatarFrame: frame }),
      });
      if (res.ok) updateUser(await res.json());
    } catch { /* offline: queda el cambio optimista en localStorage */ }
  };

  return (
    <div className="gr-tab-content">
      <div className="gr-tab-header">
        <h3 className="gr-tab-title"><FaUserCircle style={{ marginRight: 8 }} /> Avatar</h3>
        <span className="gr-tab-sub">Personalizá tu identidad P2P</span>
      </div>

      <div className="gr-avatar-stage">
        <Avatar u={u} emblem={emblem} frame={frame} size={118} />
        <div className="gr-avatar-stage-info">
          <div className="gr-avatar-stage-name">{u.name || u.username || 'Trader'}</div>
          <div className="gr-avatar-stage-sub">{emblemLabel(emblem)} · Marco {frameLabel(frame)}</div>
          <button className="gr-redeem-btn" onClick={equip} disabled={!dirty}>
            {saved ? '✓ Equipado' : dirty ? 'Equipar' : 'Equipado'}
          </button>
        </div>
      </div>

      <div className="gr-cosmetic-section">
        <h4 className="gr-earn-title"><FaStar style={{ marginRight: 8 }} /> Emblema</h4>
        <div className="gr-cosmetic-grid">
          {EMBLEMS.map((e) => {
            const ok = e.unlock(u), sel = emblem === e.key;
            return (
              <button key={e.key} disabled={!ok} onClick={() => ok && setEmblem(e.key)}
                className={`gr-cosmetic ${sel ? 'sel' : ''} ${ok ? '' : 'locked'}`}>
                <Avatar u={u} emblem={e.key} frame="basic" size={54} />
                <span className="gr-cosmetic-label">{e.label}</span>
                <span className="gr-cosmetic-req">{ok ? (sel ? 'Equipado' : 'Disponible') : <>{e.req}<FaLock size={9} style={{ marginLeft: 4 }} /></>}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="gr-cosmetic-section">
        <h4 className="gr-earn-title"><FaMedal style={{ marginRight: 8 }} /> Marco</h4>
        <div className="gr-cosmetic-grid">
          {FRAMES.map((f) => {
            const ok = f.unlock(u), sel = frame === f.key;
            return (
              <button key={f.key} disabled={!ok} onClick={() => ok && setFrame(f.key)}
                className={`gr-cosmetic ${sel ? 'sel' : ''} ${ok ? '' : 'locked'}`}>
                <div className={`gr-frame-sw ach-frame-${f.key}`}><div className="gr-frame-sw-in" /></div>
                <span className="gr-cosmetic-label">{f.label}</span>
                <span className="gr-cosmetic-req">{ok ? (sel ? 'Equipado' : 'Disponible') : <>{f.req}<FaLock size={9} style={{ marginLeft: 4 }} /></>}</span>
              </button>
            );
          })}
        </div>
        <p className="gr-note">Los emblemas y marcos se desbloquean al subir de nivel, completar trades y mantener tu racha. Tu avatar equipado te representa en todo el app.</p>
      </div>
    </div>
  );
}

function TasksTab({ u, onGoTab }) {
  const navigate = useNavigate();
  const goals = [
    { id: 'trade',  icon: FaCheck,      label: 'Completá tu primer trade',     xp: 150, done: (u.trades || 0) >= 1,    cta: () => navigate('/'),       ctaLabel: 'Ir al marketplace' },
    { id: 'verify', icon: FaShieldAlt,  label: 'Verificate como vendedor',     xp: 0,   done: !!u.verified,           cta: () => onGoTab('verify'),   ctaLabel: 'Canjear código' },
    { id: 'kyc',    icon: FaIdBadge,    label: 'Subí a KYC nivel 2',           xp: 300, done: (u.kycLevel || 0) >= 2,  cta: () => onGoTab('verify'),   ctaLabel: 'Ver verificación' },
    { id: 'lvl3',   icon: FaBolt,       label: 'Alcanzá el nivel 3',           xp: 0,   done: (u.level || 1) >= 3,     cta: () => navigate('/'),       ctaLabel: 'Ir a tradear' },
    { id: 'streak', icon: FaFire,       label: 'Mantené una racha de 7 días',  xp: 200, done: (u.streak || 0) >= 7,    cta: () => navigate('/'),       ctaLabel: 'Tradear hoy' },
    { id: 'xp',     icon: FaStar,       label: 'Acumulá 10.000 XP',            xp: 0,   done: (u.xp || 0) >= 10000,    cta: () => navigate('/'),       ctaLabel: 'Seguir sumando' },
  ];
  const done = goals.filter((g) => g.done).length;
  return (
    <div className="gr-tab-content">
      <div className="gr-tab-header">
        <h3 className="gr-tab-title"><FaTasks style={{ marginRight: 8 }} /> Tareas</h3>
        <span className="gr-tab-sub">{done}/{goals.length} completadas</span>
      </div>
      <div className="ach-progress">
        <div className="ach-progress-bar"><div className="ach-progress-fill" style={{ width: `${(done / goals.length) * 100}%` }} /></div>
        <span className="ach-progress-label">{Math.round((done / goals.length) * 100)}% completado</span>
      </div>
      <div className="gr-tasks-list">
        {goals.map((g) => {
          const Ic = g.icon;
          return (
            <div key={g.id} className={`gr-task ${g.done ? 'done' : ''}`}>
              <div className="gr-task-ic"><Ic /></div>
              <div className="gr-task-body">
                <span className="gr-task-label">{g.label}</span>
                {g.xp > 0 && <span className="gr-task-xp">+{g.xp} XP</span>}
              </div>
              {g.done
                ? <span className="gr-task-done"><FaCheck /> Listo</span>
                : <button className="gr-task-cta" onClick={g.cta}>{g.ctaLabel}</button>}
            </div>
          );
        })}
      </div>
      <p className="gr-note">El XP se acredita automáticamente al cumplir cada meta. Completalas para subir de rango más rápido.</p>
    </div>
  );
}

const TABS = [
  { key: 'progress',    label: 'Mi progreso', icon: FaBolt },
  { key: 'tasks',       label: 'Tareas',      icon: FaTasks },
  { key: 'avatar',      label: 'Avatar',      icon: FaUserCircle },
  { key: 'trophies',    label: 'Logros',      icon: FaMedal },
  { key: 'leaderboard', label: 'Ranking',     icon: FaTrophy },
  { key: 'verify',      label: 'Verificación',icon: FaCheck },
  { key: 'store',       label: 'Tienda',      icon: FaCoins },
];

// ===========================================================
// TAB: MI PROGRESO
// ===========================================================
function ProgressTab({ u }) {
  const lvl   = levelInfo(u.xp || 0);
  const color = levelColor(lvl.level);
  return (
    <div className="gr-tab-content">
      <div className="gr-xp-hero">
        <div className="gr-xp-hero-left">
          <Avatar u={u} size={68} />
          <div style={{ flex: 1 }}>
            <div className="gr-rank-pill" style={{ color, borderColor: `${color}66` }}>⬡ {lvl.rank} · Nivel {lvl.level}</div>
            <div className="gr-xp-bar-wrap" style={{ marginTop: 10 }}>
              <div className="gr-xp-bar"><div className="gr-xp-fill" style={{ width: `${lvl.pct}%`, background: color }} /></div>
              <span className="gr-xp-label">
                {(u.xp || 0).toLocaleString('es-CR')} XP
                {!lvl.isMax ? ` · faltan ${lvl.toNext.toLocaleString('es-CR')} para Nv. ${lvl.level + 1}` : ' · nivel máximo'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="gr-stat-row">
        <div className="gr-stat"><FaFire color="#ff6b35" /><b>{u.streak || 0}</b><span>días de racha</span></div>
        <div className="gr-stat"><FaCoins color="#00d412" /><b>{u.trades || 0}</b><span>trades</span></div>
        <div className="gr-stat"><FaStar color="#f0b90b" /><b>{(u.averageRating || 0).toFixed(1)}</b><span>{u.totalReviews || 0} reseñas</span></div>
        <div className="gr-stat"><FaIdBadge color="#0091ea" /><b>L{u.kycLevel || 0}</b><span>KYC</span></div>
      </div>

      <div className="gr-earn-section">
        <h4 className="gr-earn-title"><FaInfoCircle style={{ marginRight: 8 }} /> Cómo ganar XP</h4>
        <div className="gr-earn-grid">
          {XP_EARN.map((e, i) => {
            const Ic = e.icon;
            return (
              <div key={i} className="gr-earn-item">
                <span className="gr-earn-action"><Ic style={{ marginRight: 8, opacity: .8 }} />{e.action}</span>
                <div className="gr-earn-rewards"><span className="gr-earn-xp">+{e.xp} XP</span></div>
              </div>
            );
          })}
        </div>
        <p className="gr-note">El XP se otorga automáticamente al operar. Subí de nivel y rango a medida que tradeás.</p>
      </div>
    </div>
  );
}

// ===========================================================
// TAB: LOGROS
// ===========================================================
function TrophiesTab({ u }) {
  return (
    <div className="gr-tab-content">
      <div className="gr-tab-header">
        <h3 className="gr-tab-title"><FaMedal style={{ marginRight: 8 }} /> Logros</h3>
        <span className="gr-tab-sub">Algunos son secretos — desbloquealos para revelarlos</span>
      </div>
      <TrophyShowcase u={u} />
    </div>
  );
}

// ===========================================================
// TAB: RANKING (leaderboard real)
// ===========================================================
function Podium({ top3, meId }) {
  if (top3.length < 3) return null;
  const order = [top3[1], top3[0], top3[2]];
  const heights = ['80px', '110px', '60px'];
  const colors  = ['#c0c0c0', '#f0b90b', '#cd7f32'];
  return (
    <div className="gr-podium">
      {order.map((t, i) => (
        <div key={t.id} className="gr-podium-slot">
          <div className="gr-podium-avatar" style={{ background: avatarColor(t.username), boxShadow: `0 0 14px ${colors[i]}60` }}>{initials(t.username)}</div>
          <div className="gr-podium-name">{t.username}{t.id === meId && ' (vos)'}</div>
          <div className="gr-podium-vol">{(t.xp || 0).toLocaleString('es-CR')} XP</div>
          <div className="gr-podium-block" style={{ height: heights[i], background: `linear-gradient(180deg, ${colors[i]}30, ${colors[i]}10)`, borderTop: `3px solid ${colors[i]}` }}>
            <span className="gr-podium-medal" style={{ color: colors[i] }}><FaMedal size={28} /></span>
          </div>
        </div>
      ))}
    </div>
  );
}
function LeaderboardTab({ data, loading, meId }) {
  if (loading) return <div className="gr-loading">Cargando ranking…</div>;
  if (!data.length) return <div className="gr-loading">Todavía no hay traders en el ranking.</div>;
  const medalColors = { 1: '#f0b90b', 2: '#c0c0c0', 3: '#cd7f32' };
  return (
    <div className="gr-tab-content">
      <div className="gr-tab-header">
        <h3 className="gr-tab-title"><FaTrophy style={{ marginRight: 8 }} /> Top traders por XP</h3>
      </div>
      <Podium top3={data.slice(0, 3)} meId={meId} />
      <div className="gr-leaderboard-table">
        {data.map((t) => (
          <div key={t.id} className={`gr-lb-row ${t.id === meId ? 'is-me' : ''}`}>
            <span className="gr-lb-rank">
              {t.position <= 3 ? <span style={{ color: medalColors[t.position], fontSize: '1.2rem' }}><FaMedal /></span> : `#${t.position}`}
            </span>
            <div className="gr-lb-avatar" style={{ background: avatarColor(t.username) }}>{initials(t.username)}</div>
            <div className="gr-lb-info">
              <span className="gr-lb-name">{t.username} {t.id === meId && <span className="gr-lb-you">← vos</span>}</span>
              <span className="gr-lb-level" style={{ color: levelColor(t.level) }}>{t.rank} · Nv.{t.level}</span>
            </div>
            <div className="gr-lb-stats">
              <span className="gr-lb-xp"><FaBolt size={12} style={{ marginRight: 4 }} /> {(t.xp || 0).toLocaleString('es-CR')} XP</span>
              <span className="gr-lb-trades"><FaCoins size={12} style={{ marginRight: 4 }} /> {t.trades || 0}</span>
              <span className="gr-lb-rating"><FaStar size={12} style={{ marginRight: 4 }} color="#f0b90b" /> {(t.averageRating || 0).toFixed(1)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================================
// TAB: VERIFICACIÓN (real — canje de código)
// ===========================================================
function VerifyTab({ u, onVerified }) {
  const [code, setCode]       = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('');
  const { updateUser } = useAuth();

  const handleRedeem = async () => {
    if (!code.trim()) { setMessage('Ingresá un código válido'); setMsgType('error'); return; }
    setLoading(true);
    try {
      const res  = await apiFetch('/api/me/redeem-invite', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`✅ ${data.message}`); setMsgType('success'); setCode('');
        updateUser({ verified: true });
        if (onVerified) onVerified();
      } else { setMessage(`❌ ${data.message}`); setMsgType('error'); }
    } catch {
      setMessage('❌ Error al canjear el código. Probá de nuevo.'); setMsgType('error');
    } finally { setLoading(false); }
  };

  return (
    <div className="gr-tab-content">
      <div className="gr-tab-header">
        <h3 className="gr-tab-title"><FaCheck style={{ marginRight: 8 }} /> Verificación de vendedor</h3>
        <span className="gr-tab-sub">
          {u.verified ? <FaCheck style={{ marginRight: 4 }} color="#00b248" /> : <FaTimes style={{ marginRight: 4 }} color="#e53935" />}
          {u.verified ? 'Verificado' : 'No verificado'}
        </span>
      </div>
      <div className="gr-verification-card">
        <div className={`gr-verify-status ${u.verified ? 'verified' : 'unverified'}`}>
          <span className="gr-verify-icon">{u.verified ? <FaCheck size={32} color="#00b248" /> : <FaTimes size={32} color="#e53935" />}</span>
          <div className="gr-verify-text">
            <span className="gr-verify-title">{u.verified ? '¡Estás verificado!' : 'Obtené acceso de vendedor'}</span>
            <span className="gr-verify-desc">
              {u.verified ? 'Podés crear ofertas de venta en el marketplace.'
                          : 'Canjeá un código de invitación BETA para vender en Bitplify.'}
            </span>
          </div>
        </div>
      </div>
      {!u.verified && (
        <div className="gr-redeem-form">
          <h4 className="gr-redeem-title">Canjear código</h4>
          <div className="gr-redeem-input-wrap">
            <input type="text" className="gr-redeem-input" placeholder="Ej: BETA-A1B2C3" value={code}
              onChange={(e) => setCode(e.target.value)} disabled={loading}
              onKeyDown={(e) => e.key === 'Enter' && handleRedeem()} />
            <button className="gr-redeem-btn" onClick={handleRedeem} disabled={loading || !code.trim()}>
              {loading ? 'Canjeando…' : 'Canjear'}
            </button>
          </div>
          {message && <div className={`gr-redeem-message ${msgType}`}>{message}</div>}
          <div className="gr-redeem-info">
            <p><strong>¿Cómo funciona?</strong></p>
            <ul>
              <li><FaCheck color="#00b248" style={{ marginRight: 6 }} /> Pedí un código BETA a un moderador</li>
              <li><FaCheck color="#00b248" style={{ marginRight: 6 }} /> Canjealo acá para verificarte</li>
              <li><FaCheck color="#00b248" style={{ marginRight: 6 }} /> ¡Empezá a crear ofertas de venta!</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================
// TAB: TIENDA (Próximamente — el sistema de BP aún no existe)
// ===========================================================
const STORE_PREVIEW = [
  { icon: TbCrown,      name: 'Marco de avatar',        desc: 'Bordes y skins para tu perfil' },
  { icon: FaRocket,     name: 'Boost de oferta',        desc: 'Tu oferta primera en el marketplace' },
  { icon: FaShieldAlt,  name: 'Badges de confianza',    desc: 'Mostrá tu reputación' },
  { icon: FaCoins,      name: 'Gift cards & cupones',   desc: 'Canjeá tus puntos por beneficios' },
];
function StoreTab() {
  return (
    <div className="gr-tab-content">
      <div className="gr-coming-soon">
        <span className="gr-coming-badge">Próximamente</span>
        <h3><FaCoins color="#f0b90b" style={{ marginRight: 8 }} /> Tienda de recompensas</h3>
        <p>Estamos construyendo la economía de puntos para canjear skins, boosts y beneficios. Mientras tanto, segui sumando XP y subiendo de nivel.</p>
      </div>
      <div className="gr-store-grid">
        {STORE_PREVIEW.map((s, i) => {
          const Ic = s.icon;
          return (
            <div key={i} className="gr-store-card is-soon">
              <div className="gr-store-icon" style={{ color: '#8a9bb0' }}><Ic size={40} /></div>
              <div className="gr-store-name">{s.name}</div>
              <div className="gr-store-desc">{s.desc}</div>
              <div className="gr-store-footer"><span className="gr-store-owned">Pronto</span></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================
// PÁGINA
// ===========================================================
export default function GameRewards() {
  const { user } = useAuth();
  const [tab, setTab]   = useState('progress');
  const [me, setMe]     = useState(user || {});
  const [board, setBoard] = useState([]);
  const [boardLoading, setBoardLoading] = useState(true);

  // Datos frescos del usuario (XP/nivel pueden haber cambiado desde el login)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch('/api/me');
        if (res.ok && alive) setMe(await res.json());
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const reloadMe = async () => { try { const r = await apiFetch('/api/me'); if (r.ok) setMe(await r.json()); } catch {} };

  // Leaderboard real (público)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/leaderboard?limit=20`);
        if (res.ok && alive) setBoard(await res.json());
      } catch {} finally { if (alive) setBoardLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const u = { ...(user || {}), ...me };
  const lvl = levelInfo(u.xp || 0);

  return (
    <div className="gr-wrapper">
      <div className="gr-page">
        <div className="gr-page-header">
          <div>
            <h1 className="gr-page-title"><MdEmojiEvents size={30} style={{ marginRight: 12, verticalAlign: 'middle' }} /> Recompensas</h1>
            <p className="gr-page-sub">Tradeá, sumá XP y subí de nivel y rango.</p>
          </div>
          <div className="gr-rank-pill gr-header-rank" style={{ color: levelColor(lvl.level), borderColor: `${levelColor(lvl.level)}66` }}>
            ⬡ {lvl.rank} · Nv.{lvl.level}
          </div>
        </div>

        <div className="gr-tabs">
          {TABS.map((t) => {
            const Ic = t.icon;
            return (
              <button key={t.key} className={`gr-tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                <Ic size={16} style={{ marginRight: 6 }} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="gr-content">
          {tab === 'progress'    && <ProgressTab u={u} />}
          {tab === 'tasks'       && <TasksTab u={u} onGoTab={setTab} />}
          {tab === 'avatar'      && <AvatarTab u={u} />}
          {tab === 'trophies'    && <TrophiesTab u={u} />}
          {tab === 'leaderboard' && <LeaderboardTab data={board} loading={boardLoading} meId={u.id} />}
          {tab === 'verify'      && <VerifyTab u={u} onVerified={reloadMe} />}
          {tab === 'store'       && <StoreTab />}
        </div>
      </div>
    </div>
  );
}
